import {
  MINOR_SUITS,
  NUMBER_COURT,
} from "./constants";
import type {
  ICard,
  MinorSuit,
} from "./types";

type NumericMinorCourt = "simple" | "pip";

const assertNonEmptyString = (
  value: string,
  field: string,
  card: ICard
): void => {
  if (!value.trim()) {
    throw new Error(
      `Карта "${card.id || card.name}": поле "${field}" не должно быть пустым`
    );
  }
};

const assertNonEmptyStringArray = (
  values: string[],
  field: string,
  card: ICard
): void => {
  if (values.length === 0) {
    throw new Error(
      `Карта "${card.id || card.name}": массив "${field}" не должен быть пустым`
    );
  }

  values.forEach((value, index) => {
    if (!value.trim()) {
      throw new Error(
        `Карта "${card.id || card.name}": ` +
          `"${field}[${index}]" не должно быть пустым`
      );
    }
  });
};

const validateCardContent = (
  card: ICard
): void => {
  assertNonEmptyString(card.id, "id", card);
  assertNonEmptyString(card.name, "name", card);

  if (card.nameEn !== undefined) {
    assertNonEmptyString(
      card.nameEn,
      "nameEn",
      card
    );
  }

  assertNonEmptyStringArray(
    card.keywords,
    "keywords",
    card
  );

  assertNonEmptyStringArray(
    card.symbolism,
    "symbolism",
    card
  );

  assertNonEmptyString(
    card.symbolismInterpretation,
    "symbolismInterpretation",
    card
  );

  assertNonEmptyString(
    card.archetype,
    "archetype",
    card
  );

  assertNonEmptyString(
    card.psychology,
    "psychology",
    card
  );

  assertNonEmptyString(
    card.upright.personality,
    "upright.personality",
    card
  );

  assertNonEmptyString(
    card.upright.relationships,
    "upright.relationships",
    card
  );

  assertNonEmptyString(
    card.upright.work,
    "upright.work",
    card
  );

  assertNonEmptyString(
    card.upright.spirituality,
    "upright.spirituality",
    card
  );

  assertNonEmptyStringArray(
    card.upright.examples,
    "upright.examples",
    card
  );

  assertNonEmptyString(
    card.reversed.meaning,
    "reversed.meaning",
    card
  );

  if (card.reversed.nuances !== undefined) {
    assertNonEmptyString(
      card.reversed.nuances,
      "reversed.nuances",
      card
    );
  }

  assertNonEmptyStringArray(
    card.associations,
    "associations",
    card
  );

  assertNonEmptyStringArray(
    card.readingTips,
    "readingTips",
    card
  );

  assertNonEmptyStringArray(
    card.selfQuestions,
    "selfQuestions",
    card
  );

  card.combinations?.forEach(
    (combination, index) => {
      if (!combination.trim()) {
        throw new Error(
          `Карта "${card.id}": ` +
            `"combinations[${index}]" не должно быть пустым`
        );
      }
    }
  );
};

const validateMajorCard = (
  card: ICard
): void => {
  if (card.arcana !== "major") {
    throw new Error(
      `Карта "${card.id}": Major должна иметь arcana="major"`
    );
  }

  if (card.suit !== "major") {
    throw new Error(
      `Карта "${card.id}": Major должна иметь suit="major"`
    );
  }

  if (card.court !== "major") {
    throw new Error(
      `Карта "${card.id}": Major должна иметь court="major"`
    );
  }

  if (
    !Number.isInteger(card.number) ||
    card.number < 0 ||
    card.number > 21
  ) {
    throw new Error(
      `Карта "${card.id}": номер Major должен быть целым от 0 до 21`
    );
  }
};

const isMinorSuit = (
  value: ICard["suit"]
): value is MinorSuit =>
  value !== "major" &&
  MINOR_SUITS.includes(value);

const validateMinorCard = (
  card: ICard,
  numericCourtConvention?: NumericMinorCourt
): NumericMinorCourt | undefined => {
  if (card.arcana !== "minor") {
    throw new Error(
      `Карта "${card.id}": Minor должна иметь arcana="minor"`
    );
  }

  if (!isMinorSuit(card.suit)) {
    throw new Error(
      `Карта "${card.id}": некорректная Minor-масть "${card.suit}"`
    );
  }

  if (
    !Number.isInteger(card.number) ||
    card.number < 1 ||
    card.number > 14
  ) {
    throw new Error(
      `Карта "${card.id}": номер Minor должен быть целым от 1 до 14`
    );
  }

  if (card.number <= 10) {
    if (
      card.court !== "simple" &&
      card.court !== "pip"
    ) {
      throw new Error(
        `Карта "${card.id}": числовая Minor должна иметь ` +
          `court="simple" или court="pip", получено "${card.court}"`
      );
    }

    if (
      numericCourtConvention !== undefined &&
      card.court !== numericCourtConvention
    ) {
      throw new Error(
        `Карта "${card.id}": нарушена единая конвенция числовых Minor. ` +
          `Ожидалось court="${numericCourtConvention}", ` +
          `получено "${card.court}"`
      );
    }

    return card.court;
  }

  const expectedCourt =
    NUMBER_COURT[
      card.number as 11 | 12 | 13 | 14
    ];

  if (card.court !== expectedCourt) {
    throw new Error(
      `Карта "${card.id}": номер ${card.number} должен иметь ` +
        `court="${expectedCourt}", получено "${card.court}"`
    );
  }

  return numericCourtConvention;
};

export const validateCardsData = (
  majorCards: ICard[],
  minorCards: ICard[]
): void => {
  if (majorCards.length !== 22) {
    throw new Error(
      `Ожидалось 22 Major, получено ${majorCards.length}`
    );
  }

  if (minorCards.length !== 56) {
    throw new Error(
      `Ожидалось 56 Minor, получено ${minorCards.length}`
    );
  }

  const allCards = [
    ...majorCards,
    ...minorCards,
  ];

  if (allCards.length !== 78) {
    throw new Error(
      `Ожидалось 78 стандартных карт, получено ${allCards.length}`
    );
  }

  const cardIds = new Set<string>();
  const cardIdentities = new Set<string>();

  allCards.forEach((card) => {
    validateCardContent(card);

    if (cardIds.has(card.id)) {
      throw new Error(
        `Повторяющийся card.id: "${card.id}"`
      );
    }

    cardIds.add(card.id);

    const identity =
      card.arcana === "major"
        ? `major:${card.number}`
        : `minor:${card.suit}:${card.number}`;

    if (cardIdentities.has(identity)) {
      throw new Error(
        `Повторяющаяся идентичность карты: "${identity}"`
      );
    }

    cardIdentities.add(identity);
  });

  majorCards.forEach(validateMajorCard);

  let numericCourtConvention:
    | NumericMinorCourt
    | undefined;

  minorCards.forEach((card) => {
    numericCourtConvention =
      validateMinorCard(
        card,
        numericCourtConvention
      );
  });
};

